'use client';
import accountStyles from '@/components/account/AccountSurface.module.css';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import { StudioAction } from '@/components/ui/StudioAction';
import { SystemLoadingSurface } from '@/components/ui/SystemLoading';


import { SystemLoadingIcon as Loader2, SystemLoadingSwitch } from '@/components/ui/SystemLoading';
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CircleDollarSign, Gift, Headphones, LogOut, RefreshCw, Send, Trash2, X } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { Avatar } from '@/components/common';
import { SupportDialog } from '@/components/support/SupportDialog';
import { ProfileModal } from '@/components/account/ProfileModal';
import { CreditsWelcomePopover } from '@/components/credits/CreditsWelcomePopover';
import { CreditTransactionSummary } from '@/components/credits/CreditTransactionSummary';
import { useCreditsJourneyStore } from '@/lib/stores/credits-journey-store';
import { cn } from '@/lib/cn';
import { POST_LOGOUT_PATH } from '@/lib/auth/login-route';
import { useAuthStore } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';
import { useDialogFocus } from '@/lib/hooks/useDialogFocus';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useCreditTransactions, useCredits, type CreditTransaction } from '@/lib/hooks/useCredits';
import { useCreditsStore } from '@/lib/stores/credits-store';
import { delosTransition } from '@/lib/motion';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import { creatorReferralsModal } from '@/lib/stores/creator-referrals-modal-store';
import {
  resolveCurrentSubscriptionPlanTitle,
  type SubscriptionTitleLabels,
} from '@/lib/subscription-title';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { useFeatureFlag, useUserVisibleFeatureFlag } from '@/lib/stores/feature-flags-store';
import { replaceCreditUnitLabel } from '@/lib/credits/unit';
import { getCreditBalanceStatus, getCreditQuotaProgress } from '@/lib/credits/quota-health';
import { formatCreditSnapshotAt } from '@/lib/credits/journey';
import { toast } from '@/lib/stores/toast-store';

/* ================================================================
   UserAvatarMenu — 用户头像 + 弹出菜单

   从 AppSidebar 中提取，可放置在任意位置。
   默认 popover 向下弹出（适用于顶部放置）。
   ================================================================ */

interface UserAvatarMenuProps {
  className?: string;
  /** 头像尺寸，默认 40 */
  size?: number;
  /** 是否在头像旁显示用户名 */
  showInlineName?: boolean;
  variant?: 'avatar' | 'sidebar' | 'rail';
  /** 兼容旧调用；普通头像入口保留额度环，导航入口使用无外圈头像。 */
  showAvatarProgressRing?: boolean;
  /** 弹出方向：down（默认，向下）或 up（向上，用于底部放置） */
  popoverDirection?: 'down' | 'up' | 'right';
  /** 横向对齐：start = 左对齐，end = 右对齐（默认） */
  popoverAlign?: 'start' | 'end';
  /** 由父级在非关键空闲期初始化积分，避免 Canvas 首屏数据库竞争。 */
  deferCreditsInit?: boolean;
}


interface ProgressRingProps {
  label: string;
  display: string;
  progress: number;
  tone?: 'accent' | 'success' | 'warning' | 'danger';
}

const MENU_SUBSCRIPTION_TTL_MS = 30_000;

function getTransactionDelta(transaction: CreditTransaction): number {
  if (
    typeof transaction.credits_after === 'number'
    && typeof transaction.credits_before === 'number'
  ) {
    return transaction.credits_after - transaction.credits_before;
  }
  return transaction.credits_amount;
}

function getTransactionTone(transaction: CreditTransaction): 'positive' | 'negative' | 'neutral' {
  const delta = getTransactionDelta(transaction);
  if (delta > 0) return 'positive';
  if (delta < 0) return 'negative';
  return 'neutral';
}

function formatSignedNumber(value: number, locale: string): string {
  const absValue = Math.abs(value).toLocaleString(locale);
  if (value > 0) return `+${absValue}`;
  if (value < 0) return `-${absValue}`;
  return absValue;
}

function SensitiveSkeleton({ className }: { className?: string }) {
  return (
    <SystemLoadingSurface as="span"
      aria-hidden="true"
      className={cn('inline-block  rounded-md ', className)}
    />
  );
}

function getCreditProgressColor(tone: NonNullable<ProgressRingProps['tone']>): string {
  if (tone === 'success') return '#22C55E';
  if (tone === 'warning') return '#F59E0B';
  if (tone === 'danger') return '#EF4444';
  return '#4ABDE9';
}

function getCreditProgressTrackColor(tone: NonNullable<ProgressRingProps['tone']>, isActive: boolean): string {
  if (tone === 'success') return isActive ? '#BBF7D0' : '#DCFCE7';
  if (tone === 'warning') return isActive ? '#FDE68A' : '#FEF3C7';
  if (tone === 'danger') return isActive ? '#FECACA' : '#FEE2E2';
  return isActive ? '#D7F1FA' : '#E5EAF0';
}

function CreditQuotaRow({ label, remaining, quota, locale }: {
  label: string;
  remaining: number | null;
  quota: ReturnType<typeof getCreditQuotaProgress>;
  locale: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-[12px]">
        <span className="min-w-0 text-[#4B5563]">{label}</span>
        <span className="shrink-0 font-semibold tabular-nums text-[#111827]">
          {/* One text child lets a refreshed snapshot replace externally wrapped text atomically. */}
          {`${remaining?.toLocaleString(locale) ?? '—'}${quota ? ` / ${quota.limit.toLocaleString(locale)}` : ''}`}
        </span>
      </div>
      {quota ? (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E0E7EF]" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={quota.limit} aria-valuenow={Math.min(quota.remaining, quota.limit)} aria-valuetext={`${quota.remaining.toLocaleString(locale)} / ${quota.limit.toLocaleString(locale)}`}>
          <div className="h-full rounded-full bg-[#4ABDE9] transition-[width] duration-200 motion-reduce:transition-none" style={{ width: `${quota.percent}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function ProgressRing({ label, display, progress, tone = 'accent' }: ProgressRingProps) {
  const size = 42;
  const strokeWidth = 5.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalizedProgress = Math.min(Math.max(progress, 0), 1);
  const strokeDashoffset = circumference * (1 - normalizedProgress);
  const stroke = getCreditProgressColor(tone);
  const track = getCreditProgressTrackColor(tone, false);

  return (
    <span className="relative flex h-[42px] w-[42px] shrink-0 items-center justify-center" aria-label={`${label} ${display}`}>
      <svg className="h-full w-full -rotate-90" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={track}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-[stroke-dashoffset] duration-500 ease-out"
        />
      </svg>
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums',
          tone === 'warning'
            ? 'text-semantic-warning'
            : tone === 'danger'
              ? 'text-semantic-error'
              : tone === 'success'
                ? 'text-green-700'
                : 'text-[#111827]',
        )}
      >
        {display}
      </span>
    </span>
  );
}

export function UserAvatarMenu({
  className,
  size = 40,
  showInlineName = false,
  variant = 'avatar',
  popoverDirection = 'down',
  popoverAlign = 'end',
  deferCreditsInit = false,
}: UserAvatarMenuProps) {
  const { user, logout } = useAuthStore();
  const { profile, fetchProfile, reset: resetProfile } = useUserProfileStore();
  const { credits } = useCredits({ autoInit: !deferCreditsInit });
  const currentSubscription = useCreditsStore((s) => s.subscription);
  const creditsLoading = useCreditsStore((s) => s.loading);
  const creditsRefreshing = useCreditsStore((s) => s.refreshing);
  const creditsError = useCreditsStore((s) => s.error);
  const creditsUpdatedAt = useCreditsStore((s) => s.updatedAt);
  const [isOffline, setIsOffline] = useState(false);
  const accountReadUnavailable = Boolean(creditsError) || isOffline;
  useEffect(() => {
    const updateConnection = () => setIsOffline(navigator.onLine === false);
    updateConnection();
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    return () => {
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);
  const refetchCredits = useCreditsStore((s) => s.refetchCredits);
  const quote = useCreditsJourneyStore((s) => s.quote);
  const quoteUserId = useCreditsJourneyStore((s) => s.userId);
  const quoteReady = useCreditsJourneyStore((s) => s.ready);
  const t = useTranslations('nav');
  const commonT = useTranslations('common');
  const supportT = useTranslations('supportForm');
  const tPricing = useTranslations('pricing');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const isCanvas = /^\/(?:en\/|zh\/)?p\//.test(pathname ?? '');
  const welcomeOwner = useId();
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const welcomeOpen = useCreditsJourneyStore((state) => state.welcomeOwner === welcomeOwner && state.userId === user?.id);
  const showTopup = useFeatureFlag('payment_topup');
  const showSubscription = useFeatureFlag('payment_subscription');
  const showPublishingShortcut = useUserVisibleFeatureFlag('social_publishing');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuPinnedRef = useRef(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [usageHistoryOpen, setUsageHistoryOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [popoverMaxHeight, setPopoverMaxHeight] = useState<number | null>(null);
  const [popoverLeft, setPopoverLeft] = useState<number | null>(null);
  const {
    transactions: creditTransactions,
    total: creditTransactionTotal,
    loading: creditTransactionsLoading,
    error: creditTransactionsError,
    hasMore: creditTransactionsHasMore,
    loadMore: loadMoreCreditTransactions,
    refetch: refetchCreditTransactions,
  } = useCreditTransactions(20, usageHistoryOpen);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  const focusAccountMenuRef = useRef(false);
  useBodyScrollLock(usageHistoryOpen);
  useDialogFocus(historyRef, usageHistoryOpen, () => setUsageHistoryOpen(false));
  const closeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (user?.id) {
      fetchProfile();
      return;
    }
    resetProfile();
  }, [fetchProfile, resetProfile, user?.id]);

  // 点击外部关闭
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (isLoggingOut) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLoggingOut, menuOpen]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setPopoverMaxHeight(null);
      setPopoverLeft(null);
      return;
    }

    const updatePopoverMaxHeight = () => {
      const rect = menuRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportPadding = 16;
      const popoverGap = 8;
      const width = Math.min(336, window.innerWidth - 24);
      const desiredLeft = popoverDirection === 'right'
        ? rect.right + popoverGap
        : popoverAlign === 'start' ? rect.left : rect.right - width;
      setPopoverLeft(Math.max(12, Math.min(desiredLeft, window.innerWidth - width - 12)) - rect.left);

      const availableHeight = popoverDirection === 'up'
        ? rect.top - viewportPadding - popoverGap
        : popoverDirection === 'right'
          ? popoverAlign === 'end' ? rect.bottom - viewportPadding : window.innerHeight - rect.top - viewportPadding
          : window.innerHeight - rect.bottom - viewportPadding - popoverGap;

      setPopoverMaxHeight(Math.max(0, Math.floor(availableHeight)));
    };

    updatePopoverMaxHeight();
    window.addEventListener('resize', updatePopoverMaxHeight);
    window.addEventListener('scroll', updatePopoverMaxHeight, true);

    return () => {
      window.removeEventListener('resize', updatePopoverMaxHeight);
      window.removeEventListener('scroll', updatePopoverMaxHeight, true);
    };
  }, [menuOpen, popoverDirection, popoverAlign]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const prepareAccountOverlay = useCallback(() => {
    clearCloseTimer();
    // Menu rows unmount on handoff; modal focus restoration needs a stable opener.
    avatarButtonRef.current?.focus({ preventScroll: true });
    setMenuOpen(false);
  }, [clearCloseTimer]);

  const refreshMenuData = useCallback(() => {
    const state = useCreditsStore.getState();
    if (!state.loading && !state.refreshing
      && (!state.updatedAt || Date.now() - state.updatedAt >= MENU_SUBSCRIPTION_TTL_MS || state.error)) {
      // The shared store owns errors, request coalescing and the last successful snapshot.
      void state.refetchCredits({ silent: true });
    }
  }, []);

  const openMenu = useCallback(() => {
    if (isLoggingOut) return;
    clearCloseTimer();
    if (!menuOpen) {
      refreshMenuData();
    }
    setMenuOpen(true);
  }, [clearCloseTimer, isLoggingOut, menuOpen, refreshMenuData]);

  const closeMenuSoon = useCallback(() => {
    clearCloseTimer();
    if (menuPinnedRef.current || menuRef.current?.contains(document.activeElement)) return;
    closeTimerRef.current = window.setTimeout(() => {
      setMenuOpen(false);
      closeTimerRef.current = null;
    }, 240);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!menuOpen) menuPinnedRef.current = false;
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      clearCloseTimer();
      setMenuOpen(false);
      avatarButtonRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, clearCloseTimer]);

  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      toast.showError(error, 'auth', { fallbackMessage: commonT('error') });
    } finally {
      setMenuOpen(false);
      window.location.replace(POST_LOGOUT_PATH);
    }
  }, [commonT, isLoggingOut, logout]);

  const navigateFromMenu = useCallback((path: string) => {
    clearCloseTimer();
    setMenuOpen(false);
    router.push(path);
  }, [clearCloseTimer, router]);

  const userEmail = profile?.email || user?.email || 'User';
  const displayName = profile?.display_name || userEmail.split('@')[0] || t('defaultUserName');
  const avatarUrl = profile?.avatar_url || null;
  const subscriptionTitleLabels: SubscriptionTitleLabels = {
    free: tPricing('tierFree'),
    starter: tPricing('appStarterPlanDisplayName'),
    basic: tPricing('basicPlanName'),
    pro: tPricing('proPlanName'),
    ultimate: tPricing('ultimatePlanName'),
    enterprise: tPricing('tierEnterprise'),
  };
  const tierLabel = resolveCurrentSubscriptionPlanTitle({
    subscription: currentSubscription,
    labels: subscriptionTitleLabels,
  });
  const displayedTierLabel = tierLabel ?? (accountReadUnavailable ? t('creditsPlanUnavailable') : null);
  const unitText = useCallback((text: string) => replaceCreditUnitLabel(text, credits, locale), [credits, locale]);
  const balance = typeof credits?.credits_balance === 'number' ? credits.credits_balance : null;
  const subscriptionCurrent = credits?.subscription_credits_remaining ?? null;
  const subscriptionMonthly = currentSubscription?.plan?.credits_per_month ?? null;
  const projectId = pathname?.match(/^\/(?:en\/|zh\/)?p\/([^/]+)/)?.[1];
  const currentQuote = quoteReady && quoteUserId === user?.id && quote?.projectId === projectId && !accountReadUnavailable && !creditsLoading
    ? quote?.credits : null;
  const balanceStatus = getCreditBalanceStatus(balance, currentQuote);
  const isLow = !accountReadUnavailable && (balanceStatus === 'insufficient' || balanceStatus === 'exhausted');
  const balanceText = balance !== null ? balance.toLocaleString(locale) : null;
  const isMonthlyExhausted = subscriptionCurrent === 0 && (subscriptionMonthly ?? 0) > 0;
  const balanceStatusText = isOffline ? t('creditsOfflineShort') : accountReadUnavailable ? t(balance === null ? 'creditsLoadFailedShort' : 'creditsStaleShort')
    : balanceStatus === 'exhausted' ? t('creditsExhausted')
      : balanceStatus === 'insufficient' ? t('creditsQuoteInsufficient')
        : balanceStatus === 'low' ? t('creditsQuoteLow')
          : isMonthlyExhausted ? t('creditsMonthlyExhausted') : null;
  const allowanceQuota = getCreditQuotaProgress(subscriptionCurrent, subscriptionMonthly);
  const allowanceLabel = unitText(t('monthlyCredits'));
  const ringTone = accountReadUnavailable ? 'warning'
    : isLow ? 'danger'
      : isMonthlyExhausted || balanceStatus === 'low' || (allowanceQuota && allowanceQuota.percent <= 60) ? 'warning'
        : allowanceQuota ? 'success' : 'accent';
  const ringColor = getCreditProgressColor(ringTone);
  const ringTrackColor = getCreditProgressTrackColor(ringTone, menuOpen);
  const ringDegrees = allowanceQuota ? allowanceQuota.progress * 360 : null;
  const menuLabel = [unitText(t('creditsMenuLabel', { balance: balanceText ?? t(accountReadUnavailable ? 'creditsLoadFailedShort' : 'creditsLoadingShort') })), balanceStatusText].filter(Boolean).join('. ');
  const ringLabel = allowanceQuota ? t('creditsAllowanceRing', { percent: Math.round(allowanceQuota.percent) }) : null;

  const snapshotTime = formatCreditSnapshotAt(creditsUpdatedAt ? new Date(creditsUpdatedAt).toISOString() : null, locale);
  const showPlanAction = showSubscription || showTopup;
  const openPlans = () => {
    setMenuOpen(false);
    pricingModal.open({
      triggerReason: showSubscription ? 'upgrade' : 'manual',
      defaultTab: showSubscription ? 'upgrade' : 'topup',
    });
  };
  const showAdminDashboard = user?.isAdmin;
  const workspaceShortcutItems = [
    ...(showPublishingShortcut ? [{
      key: 'publishing',
      label: t('publishing'),
      icon: Send,
      path: '/publishing',
    }] : []),
  ];
  const hasCreditsSnapshot = credits !== null;
  const showTierSkeleton = displayedTierLabel === null && !accountReadUnavailable;

  return (
    <div
      className={cn('relative', className)}
      ref={menuRef}
      onMouseEnter={welcomeOpen ? undefined : openMenu}
      onMouseLeave={closeMenuSoon}
    >
      <button
        ref={avatarButtonRef}
        type="button"
        onClick={() => {
          if (isLoggingOut) return;
          if (welcomeOpen) useCreditsJourneyStore.getState().hideWelcome();
          if (menuOpen && menuPinnedRef.current) {
            clearCloseTimer();
            setMenuOpen(false);
          } else {
            menuPinnedRef.current = true;
            openMenu();
          }
        }}
        className={cn(
          'group flex items-center transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2',
          variant === 'sidebar'
            ? 'w-full justify-start gap-2 rounded-lg px-1 py-1.5 text-left hover:bg-[#E8EDF2] focus-visible:ring-offset-0'
            : variant === 'rail'
            ? 'h-11 w-11 justify-center rounded-full hover:bg-[#E8EDF2] focus-visible:ring-offset-0'
            : showInlineName
            ? 'justify-center p-[3px] max-w-[180px] overflow-hidden rounded-[12px] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_14px_rgba(74,189,233,0.18)]'
            : 'justify-center p-[3px] overflow-hidden rounded-full shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:shadow-[0_4px_14px_rgba(74,189,233,0.18)]',
        )}
        style={variant !== 'avatar' ? undefined : {
          minHeight: size + 6,
          width: showInlineName ? undefined : size + 6,
          height: showInlineName ? undefined : size + 6,
          background: accountReadUnavailable ? ringTrackColor
            : ringDegrees !== null ? `conic-gradient(from -90deg, ${ringColor} 0deg ${ringDegrees}deg, ${ringTrackColor} ${ringDegrees}deg 360deg)`
              : '#E5EAF0',
          ...(accountReadUnavailable ? { boxShadow: `inset 0 0 0 1px ${ringColor}` } : {}),
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
            focusAccountMenuRef.current = true;
            if (event.key.startsWith('Arrow')) { event.preventDefault(); menuPinnedRef.current = true; openMenu(); }
          }
        }}
        aria-haspopup="dialog"
        data-account-trigger={variant}
        data-credit-avatar-ring={variant === 'avatar' ? true : undefined}
        aria-label={[menuLabel, ringLabel].filter(Boolean).join('. ')}
        title={[menuLabel, ringLabel].filter(Boolean).join('. ')}
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? `${welcomeOwner}-account-menu` : undefined}
        disabled={isLoggingOut}
      >
        <span className="sr-only">{balanceText ?? t(accountReadUnavailable ? 'creditsLoadFailedShort' : 'creditsLoadingShort')}</span>
        {variant === 'sidebar' ? <>
          <Avatar src={avatarUrl} userId={profile?.user_id || user?.id || null} name={displayName} email={userEmail} size={size} textClassName={size >= 40 ? 'text-base' : 'text-sm'} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium leading-4 text-[#263445]">{displayName}</span>
            {displayedTierLabel && <span className="block truncate text-[11px] leading-4 text-[#738194]">{displayedTierLabel}</span>}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[#98A2B3]" aria-hidden />
        </> : variant === 'rail' ? (
          <Avatar src={avatarUrl} userId={profile?.user_id || user?.id || null} name={displayName} email={userEmail} size={size} textClassName={size >= 40 ? 'text-base' : 'text-sm'} />
        ) : <span
          className={cn(
            'flex shrink-0 items-center justify-center overflow-hidden',
            showInlineName
              ? 'min-w-0 gap-1.5 rounded-[9px] bg-white/95 py-[2px] pl-2.5 pr-[2px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.7)] backdrop-blur-sm transition-colors duration-150 group-hover:bg-white'
              : 'rounded-full bg-white ring-[1.5px] ring-white/90',
          )}
          style={showInlineName ? { minHeight: size } : { width: size, height: size }}
        >
          {showInlineName ? <span className="min-w-0 max-w-[120px] truncate text-[13px] font-semibold leading-none text-[#111827]">{displayName}</span> : null}
          <span className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-full', showInlineName ? 'ring-[1.5px] ring-black/[0.08]' : 'h-full w-full')} style={showInlineName ? { width: size, height: size } : undefined}>
            <Avatar src={avatarUrl} userId={profile?.user_id || user?.id || null} name={displayName} email={userEmail} size={size} className="h-full w-full" textClassName={size >= 40 ? 'text-base' : 'text-sm'} />
          </span>
        </span>}
      </button>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{balanceStatusText}</span>
      <CreditsWelcomePopover owner={welcomeOwner} anchorRef={avatarButtonRef} autoShow={isCanvas} blocked={menuOpen || profileModalOpen || usageHistoryOpen} />

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            ref={menuContentRef}
            role="dialog"
            aria-label={menuLabel}
            onAnimationComplete={() => {
              if (focusAccountMenuRef.current) {
                menuContentRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
                focusAccountMenuRef.current = false;
              }
            }}
            onBlur={event => { if (event.relatedTarget && !menuRef.current?.contains(event.relatedTarget as Node)) setMenuOpen(false); }}
            onKeyDown={event => {
              if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setMenuOpen(false); avatarButtonRef.current?.focus({ preventScroll: true }); return; }
              if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
              const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled),a[href]')).filter(element => element.getClientRects().length > 0);
              const index = controls.indexOf(document.activeElement as HTMLElement);
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? controls.length - 1 : (index + (event.key === 'ArrowUp' ? -1 : 1) + controls.length) % controls.length;
              event.preventDefault(); controls[next]?.focus();
            }}
            id={`${welcomeOwner}-account-menu`}
            onMouseEnter={clearCloseTimer}
            initial={{ opacity: 0, y: popoverDirection === 'up' ? 4 : popoverDirection === 'right' ? 0 : -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: popoverDirection === 'up' ? 4 : popoverDirection === 'right' ? 0 : -4, scale: 0.96 }}
            transition={delosTransition.micro}
            style={{ ...(popoverMaxHeight !== null ? { maxHeight: `${popoverMaxHeight}px` } : {}), ...(popoverLeft !== null ? { left: popoverLeft, right: 'auto' } : {}) }}
            className={cn(
              'absolute z-[220] isolate',
              popoverDirection === 'up'
                ? 'bottom-full mb-2'
                : popoverDirection === 'right'
                  ? popoverAlign === 'end' ? 'bottom-0' : 'top-0'
                  : cn(
                    'top-full mt-2',
                    popoverAlign === 'start' ? 'left-0' : 'right-0',
                  ),
              'w-[min(21rem,calc(100vw-1.5rem))] max-h-[min(36rem,calc(100vh-6rem))]',
              'overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
            )}
          >
            <LiquidGlass className={`${accountStyles.menu} p-2`}>
              <button
                type="button"
                onClick={() => {
                  prepareAccountOverlay();
                  setProfileModalOpen(true);
                }}
	                className="group flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left transition-colors hover:bg-[#F7F8FA]"
              >
                <Avatar
                  src={avatarUrl}
                  userId={profile?.user_id || user?.id || null}
                  name={displayName}
                  email={userEmail}
                  size={38}
                  textClassName="text-sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold leading-tight text-[#111827]">{displayName}</p>
                  <p className="mt-0.5 truncate text-[12px] text-[#6B7280]">{userEmail}</p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#9CA3AF] transition-transform group-hover:translate-x-0.5" />
              </button>

              <div className="border-b border-[#EEF1F5] px-3 pb-3.5 pt-2">
                <div className="flex items-center justify-between gap-3">
                  {showTierSkeleton ? (
                    <SensitiveSkeleton className="h-[24px] w-28" />
                  ) : (
                    <span className="min-w-0 truncate text-[20px] font-bold leading-tight text-[#111827]">{displayedTierLabel}</span>
                  )}
                  {(showAdminDashboard || showPlanAction) ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                      {showAdminDashboard ? (
                        <StudioAction
                          size="compact"
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            window.location.href = '/admin';
                          }}
                          className="min-h-8 px-3 text-[12px]"
                        >
                          {t('admin')}
                        </StudioAction>
                      ) : null}
                      {showPlanAction ? (
                        <StudioAction size="compact"
                          type="button"
                          onClick={openPlans}
                          className={cn('gap-2 text-[12px]', isLow && accountStyles.lowBalanceAction)}
                        >
                          {tPricing('upgrade')}
                        </StudioAction>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mt-3 border-t border-dashed border-[#D7DEE8]" />

                <div className="mt-4 space-y-3" aria-busy={creditsLoading || creditsRefreshing}>
                  <button
                    type="button"
                    onClick={openPlans}
                    disabled={!showPlanAction}
                    className={cn(
                      'group flex w-full items-center justify-between gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/50',
                      showPlanAction && '-mx-2 w-[calc(100%+1rem)] px-2 py-1.5 transition-colors hover:bg-[#F3F6F9]',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-[14px] font-bold text-[#111827]">
                      <CircleDollarSign className="h-5 w-5 shrink-0 text-[#4ABDE9]" strokeWidth={1.9} aria-hidden />
                      <span>{unitText(t('creditsTotalAvailable'))}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {balanceText !== null ? (
                        <span className={cn('text-[20px] font-semibold tabular-nums', isLow ? 'text-semantic-error' : 'text-[#111827]')}>{balanceText}</span>
                      ) : accountReadUnavailable ? <span className="text-sm text-[#64748B]">—</span> : <SensitiveSkeleton className="h-6 w-20" />}
                      {showPlanAction ? <ChevronRight className="h-4 w-4 text-[#9CA3AF] transition-transform group-hover:translate-x-0.5" aria-hidden /> : null}
                    </span>
                  </button>
                  {accountReadUnavailable ? (
                    <div className="rounded-lg bg-amber-50 p-2.5 text-[12px] leading-4 text-amber-900" role="status">
                      <p>{t(isOffline ? (hasCreditsSnapshot ? 'creditsOfflineSnapshot' : 'creditsOfflineInitial') : (hasCreditsSnapshot ? 'creditsRefreshFailed' : 'creditsLoadFailed'))}</p>
                      {snapshotTime ? <p className="mt-1">{t('creditsLastUpdated', { time: snapshotTime })}</p> : null}
                      <button type="button" onClick={() => { void refetchCredits(); }} disabled={creditsLoading || creditsRefreshing} className="mt-2 inline-flex min-h-8 items-center gap-1.5 rounded-md border border-amber-300 px-2 font-semibold disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]">
                        <RefreshCw className={cn('h-3.5 w-3.5', (creditsLoading || creditsRefreshing) && 'animate-spin')} aria-hidden />
                        {t(creditsLoading || creditsRefreshing ? 'creditsRefreshing' : 'retry')}
                      </button>
                    </div>
                  ) : creditsRefreshing ? <p className="text-[11px] text-[#64748B]" role="status">{t('creditsRefreshing')}</p> : null}
                  {!accountReadUnavailable && (isLow || balanceStatus === 'low') && balanceStatusText ? <p className={cn('rounded-lg px-2.5 py-2 text-[12px] font-medium', isLow ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-900')}>{balanceStatusText}</p> : null}
                  {hasCreditsSnapshot ? (
                    <div className="rounded-lg bg-[#F7F9FB] p-2.5">
                      <CreditQuotaRow label={allowanceLabel} remaining={allowanceQuota?.remaining ?? null} quota={allowanceQuota} locale={locale} />
                    </div>
                  ) : !accountReadUnavailable ? (
                    <div aria-label={t('creditsLoadingShort')}>
                      <SensitiveSkeleton className="h-4 w-full" />
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => {
                      prepareAccountOverlay();
                      setUsageHistoryOpen(true);
                    }}
                    className="group -ml-1 flex min-h-8 items-center gap-1 rounded-lg px-1 text-[13px] font-semibold text-[#4B5563] transition-colors hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
                  >
                    <span>{t('viewUsage')}</span>
                    <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.1} aria-hidden />
                  </button>

                </div>
              </div>

              <div className="mt-1 space-y-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    creatorReferralsModal.open('redeem');
                  }}
                  className="group flex min-h-9 w-full items-center gap-2.5 rounded-[12px] px-3 text-left text-[13px] font-semibold text-[#111827] transition-colors hover:bg-[#F3F6F9]"
                >
                  <Gift className="h-4 w-4 shrink-0 text-hr-text-primary" />
                  <span className="min-w-0 flex-1 truncate">{t('redeemInviteCode')}</span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF] opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                </button>
              </div>

              <div className="mt-1">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    window.location.href = '/trash';
                  }}
                  className="group flex min-h-9 w-full items-center gap-2.5 rounded-[12px] px-3 text-left text-[13px] font-semibold text-[#111827] transition-colors hover:bg-[#F3F6F9]"
                >
                  <Trash2 className="h-4 w-4 shrink-0 text-hr-text-primary" />
                  <span className="min-w-0 flex-1 truncate">{t('deletedRecords')}</span>
                </button>
              </div>

              {workspaceShortcutItems.length > 0 ? (
                <div className="mt-1 space-y-0.5">
                  {workspaceShortcutItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        aria-label={item.label}
                        title={item.label}
                        onClick={() => navigateFromMenu(item.path)}
                        className="group flex min-h-9 w-full items-center gap-2.5 rounded-[12px] px-3 text-left text-[13px] font-semibold text-[#111827] transition-colors hover:bg-[#F3F6F9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-hr-text-primary" strokeWidth={1.9} aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF] opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="mt-1">
                <button type="button" onClick={() => { prepareAccountOverlay(); setSupportOpen(true); }}
                  className="group flex min-h-9 w-full items-center gap-2.5 rounded-[12px] px-3 text-left text-[13px] font-semibold text-[#111827] transition-colors hover:bg-[#EFF9FD] hover:text-[#167D9F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35">
                  <Headphones className="h-4 w-4 shrink-0 text-hr-text-primary" aria-hidden />
                  <span>{supportT('title')}</span>
                </button>
              </div>

              <div className="mt-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className={cn(
                    'flex min-h-9 w-full items-center gap-2.5 rounded-[12px] px-3 text-left text-[13px] font-semibold transition-colors',
                    isLoggingOut
                      ? 'cursor-wait bg-accent-core/6 text-accent-core'
                      : 'text-semantic-error hover:bg-semantic-error-bg',
                  )}
                >
                  {isLoggingOut ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{isLoggingOut ? t('signingOut') : t('signOut')}</span>
                </button>
              </div>
            </LiquidGlass>
          </motion.div>
        )}
      </AnimatePresence>

      {typeof document !== 'undefined' ? createPortal(
        <AnimatePresence>
          {usageHistoryOpen ? (
            <motion.div
              data-credit-usage-history-overlay
              className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-950/18 px-3 py-4 backdrop-blur-[2px] sm:px-4 sm:py-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={delosTransition.micro}
              onMouseEnter={clearCloseTimer}
              onClick={() => setUsageHistoryOpen(false)}
            >
              <motion.div
                ref={historyRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="credit-usage-history-title"
                className={`${accountStyles.history} flex max-h-[calc(100dvh-32px)] w-full max-w-[520px] flex-col overflow-hidden rounded-[10px] border border-[#D7DEE8] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.28)] sm:max-h-[min(680px,calc(100dvh-48px))]`}
                initial={{ y: 10, scale: 0.98 }}
                animate={{ y: 0, scale: 1 }}
                exit={{ y: 10, scale: 0.98 }}
                transition={delosTransition.micro}
                onClick={(event) => event.stopPropagation()}
              >
              <LiquidGlass className={accountStyles.modalLens}><span /></LiquidGlass>
              <div className="flex items-start justify-between gap-4 border-b border-[#EEF1F5] px-5 py-4">
                <div className="min-w-0">
                  <h2 id="credit-usage-history-title" className="text-[18px] font-bold leading-6 text-[#111827]">
                    {unitText(t('usageHistoryTitle'))}
                  </h2>
                  <p className="mt-1 text-[12px] leading-5 text-[#6B7280]">
                    {unitText(t('usageHistorySubtitle'))}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => refetchCreditTransactions()}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#6B7280] transition hover:bg-[#F3F6F9] hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
                    aria-label={t('usageHistoryRefresh')}
                  >
                    <SystemLoadingSwitch icon={RefreshCw} className={cn('h-4 w-4', creditTransactionsLoading && 'animate-spin')} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setUsageHistoryOpen(false)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#6B7280] transition hover:bg-[#F3F6F9] hover:text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9]/35"
                    aria-label={t('usageHistoryClose')}
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                {creditTransactionsError ? (
                  <div className="rounded-[10px] border border-semantic-error/20 bg-semantic-error-bg px-4 py-3 text-[13px] font-semibold text-semantic-error">
                    {t('usageHistoryLoadFailed')}
                  </div>
                ) : null}

                {creditTransactionsError && creditTransactions.length === 0 ? null : creditTransactionsLoading && creditTransactions.length === 0 ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((item) => (
                      <div key={item} className="rounded-[10px] border border-[#EEF1F5] px-4 py-3">
                        <SensitiveSkeleton className="h-4 w-32" />
                        <SensitiveSkeleton className="mt-2 h-4 w-full" />
                        <SensitiveSkeleton className="mt-2 h-3 w-24" />
                      </div>
                    ))}
                  </div>
                ) : creditTransactions.length === 0 ? (
                  <div className="rounded-[10px] border border-dashed border-[#D7DEE8] px-5 py-10 text-center">
                    <CircleDollarSign className="mx-auto h-8 w-8 text-[#9CA3AF]" strokeWidth={1.8} />
                    <p className="mt-3 text-[14px] font-semibold text-[#111827]">{t('usageHistoryEmptyTitle')}</p>
                    <p className="mt-1 text-[12px] leading-5 text-[#6B7280]">{unitText(t('usageHistoryEmptyDescription'))}</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {creditTransactions.map((transaction) => {
                      const delta = getTransactionDelta(transaction);
                      const tone = getTransactionTone(transaction);
                      return (
                        <div key={transaction.id} className="rounded-[10px] border border-[#EEF1F5] bg-white px-4 py-3 shadow-[0_4px_14px_rgba(15,23,42,0.04)]">
                          <div className="flex items-start justify-between gap-4">
                            <CreditTransactionSummary transaction={transaction} locale={locale} />
                            <span
                              className={cn(
                                'shrink-0 text-[15px] font-bold tabular-nums',
                                tone === 'positive'
                                  ? 'text-green-600'
                                  : tone === 'negative'
                                    ? 'text-semantic-error'
                                    : 'text-[#6B7280]',
                              )}
                            >
                              {formatSignedNumber(delta, locale)}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-3 text-[11px] leading-4 text-[#8A93A2]">
                            <span>{unitText(t('usageHistoryBalanceAfter'))}</span>
                            <span className="font-medium tabular-nums text-[#6B7280]">
                              {transaction.credits_after.toLocaleString(locale)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-[#EEF1F5] px-5 py-3">
                <span className="text-[12px] text-[#6B7280]">
                  {t('usageHistoryTotal', { count: creditTransactionTotal })}
                </span>
                {creditTransactionsHasMore ? (
                  <StudioAction size="compact"
                    type="button"
                    onClick={loadMoreCreditTransactions}
                    disabled={creditTransactionsLoading}
                    className="gap-2 text-[12px]"
                  >
                    {creditTransactionsLoading ? t('usageHistoryLoadingMore') : t('usageHistoryLoadMore')}
                  </StudioAction>
                ) : null}
              </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body,
      ) : null}

      {supportOpen && <SupportDialog key={user?.id} onClose={() => setSupportOpen(false)} />}

      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </div>
  );
}
